// deps
import { useRef, useMemo, useEffect } from "react";

// internal deps
import { isEmpty } from "@swiggy-private/js-utils";
import { init, addRequestInterceptor, addResponseInterceptor, mockWebSdk } from "@swiggy-private/native-bridge";
import Blaze from "@swiggy-private/blaze.js";
import { NativeRequest } from "@swiggy-private/native-bridge/dist/interfaces";

// utils
import { getAppHostName, getUserAgent } from "helpers/api";
import { initBridgeSdk, isBrowserEnv } from "utils/bridgeSdk";

// hooks
import WebBridgeSdk from "utils/webBridgeSdk";
import packageJson from "../package.json";
import { useEffectOnce } from "./useEffectOnce";

// helpers

// initalise Native Bridge SDK
export const Sdk = init();
export const WebBridge = WebBridgeSdk.setUpWebSdk();

const EMPTY_FUNCTION = () => void 0;

const getPrefix = (prefix?: string): string => (!isEmpty(prefix) ? `${prefix}` : "");

const createNBRequestInterceptor = (recordNREvent?: Function, prefix?: string): VoidFunction =>
    addRequestInterceptor((req: NativeRequest<{ state?: string }>) => {
        if (recordNREvent == null) {
            return;
        }
        const projectPrefix = getPrefix(prefix);

        recordNREvent({
            eventType: `${projectPrefix}_web_native_bridge_request`,
            id: req.requestId,
            type: req.type,
            subType: req.payload?.state ?? "",
        });
    });

const createNBResponseInterceptor = (recordNREvent?: Function, prefix?: string): VoidFunction =>
    addResponseInterceptor((res) => {
        if (recordNREvent == null) {
            return;
        }
        const projectPrefix = getPrefix(prefix);

        recordNREvent({
            eventType: `${projectPrefix}_web_native_bridge_response`,
            id: res.requestId,
            type: res.type,
            statusCode: res.statusCode ?? -1,
            statusMessage: res.statusMessage ?? "",
            bridgeType: res.bridgeType ?? "",
        });
    });

export function useBootstrap(nbEventTypePrefix: string, isCached?: boolean): void {
    const initRef = useRef(false);

    useEffectOnce(() => {
        if (isBrowserEnv() && process.env.NODE_ENV !== "production") {
            mockWebSdk();
        }
    });

    if (!initRef.current) {
        // init blaze
        Blaze.init({
            sendToAnalytics: EMPTY_FUNCTION,
            enableResourceTiming: false,
            nrConfig: {
                enable: true,
                config: {
                    NR_ENDPOINT: process.env.NEXT_PUBLIC_NEW_RELIC_ENDPOINT ?? "", // enter newrelic endpoint here
                    NR_KEY: process.env.NEXT_PUBLIC_NEW_RELIC_KEY ?? "", // enter newrelic key here
                },
                meta: {
                    appName: nbEventTypePrefix, // enter app name
                    /* JS hash is generated without replacing with actual value
                    So cached file would be same for both canary vs non-canary, and would have unknown behavior */
                    appDeployName: isBrowserEnv()
                        ? (document?.querySelector("[data-app-name]") as HTMLDivElement)?.dataset.appName ?? ""
                        : "",
                    ...(isCached && { isHtmlCached: String(isCached) }),
                    appVersion: packageJson.version,
                    appHostName: getAppHostName(),
                    appEnvName: process.env.NODE_ENV ? process.env.NODE_ENV : "",
                    userAgent: getUserAgent(),
                },
            },
        });

        // create function to intialise NB
        initBridgeSdk(Sdk);
    }

    const requestInterceptor = useMemo(
        () => createNBRequestInterceptor(Blaze.recordCustomEvent, nbEventTypePrefix),
        [nbEventTypePrefix],
    );
    const responseInterceptor = useMemo(
        () => createNBResponseInterceptor(Blaze.recordCustomEvent, nbEventTypePrefix),
        [nbEventTypePrefix],
    );

    useEffectOnce(() => {
        initRef.current = true;
    });

    useEffect(() => {
        return (): void => {
            requestInterceptor();
            responseInterceptor();
        };
    }, [requestInterceptor, responseInterceptor]);
}
